import React from 'react';
import styled from 'styled-components';
import {
  Button,
  Icon,
  Menu,
  MenuCap,
  MenuItem,
  MenuState,
  MenuTrailingLabel,
  Search,
} from '@repo/design-system';
import type { IconName, IconVariant } from '@repo/design-system';
import { space, zIndex } from '@repo/design-tokens';
import type { ManufacturerCatalogStatus } from '@repo/front-end-state';
import { visuallyHidden } from '@repo/ui';

/**
 * The wizard's title-bar search shell (`onboarding-polish-search`, spec FR3; shared by
 * `composites-page-search`, spec FR1): the design system's `Search` as a combobox over whatever the
 * step matches, with its hits listed in the design system's `Menu` panel. A best-effort design from
 * existing design-system components, by Rob's ruling of 2026-10-05, for his UX review once the steps
 * are complete — not a new component.
 *
 * Each step binds it: the Floor plan and Observers steps through `PlanEntitySearch` (plan entities),
 * the Composite measures step through `CompositeTotalSearch` (totals). What a hit IS, how it reads
 * and what a pick DOES are the binding's; this only owns the behaviour, so the three steps' boxes
 * cannot drift apart.
 *
 * Keyboard (FR3.3, extended by `floor-plan-page-search`): ↓/↑ move the highlight, wrapping, over
 * every row — an unavailable row too, so its words are heard. Enter on a highlighted row picks it,
 * or, if it cannot be picked (`isPickable`), announces why (`unpickableText`). With nothing
 * highlighted, Enter picks the only hit, or explains it when it cannot be picked; with two or more
 * hits it hands the query to the host's `onSubmit` (the Floor plan frames them), which closes the
 * list while focus, the query and the host's light stay. Escape closes the list, and a second
 * Escape clears the box (`Search`'s own Escape, reached only once the list is closed); Tab leaves
 * and closes. Focus never leaves the input — the highlight is `aria-activedescendant`.
 *
 * **The highlight is a KEY, not a position.** The list re-ranks under the operator's hands —
 * equipment types land seconds after the first focus and pull new machines in, the register arrives,
 * the plan changes — and a remembered index would then point at a different row: Enter would pick
 * something the operator never chose (found by review). A highlighted hit that leaves the list
 * is simply no longer highlighted.
 *
 * Equipment types arrive later than the plan (the catalogue loads on the box's first focus), so
 * `typeStatus` puts a note at the foot of the list while they are loading or could not be loaded —
 * code, name and manufacturer search work throughout.
 */

export type TitleBarSearchTypeStatus = ManufacturerCatalogStatus;

/** How one hit reads in the list. */
export interface TitleBarSearchOption {
  icon: IconName;
  /** The glyph's weight; absent: the icon's default (outline). */
  iconVariant?: IconVariant;
  /** The primary line. */
  label: string;
  /** The line under it. */
  meta: string;
  /** The row's trailing slot. */
  trailing?: string;
  /**
   * Draws `trailing` as the design system's lavender trailing word — the Floor plan's *Not on plan*
   * (`floor-plan-page-search`, R10.4). Absent, the trailing string is plain, as before.
   */
  trailingTone?: 'lav';
  /** The option's accessible name — everything the row says, in words. */
  name: string;
}

export interface TitleBarSearchProps<T> {
  /** The box's accessible name. */
  label: string;
  placeholder: string;
  /** What the query finds, best first; `total` counts every match, so a capped list can say so. */
  match: (query: string) => { hits: ReadonlyArray<T>; total: number };
  /** A hit's stable identity — what the highlight holds. */
  keyOf: (hit: T) => string;
  option: (hit: T) => TitleBarSearchOption;
  /** The list's sentence when the query finds nothing. */
  emptyText: (query: string) => string;
  /** What is announced once a hit is picked. */
  pickedText: (hit: T) => string;
  onPick: (hit: T) => void;
  /**
   * Loads the equipment types: on the box's first focus, from the list's Retry, and on any focus
   * while `typeStatus` is `error` — the keyboard's way to retry, because the Retry button inside the
   * list cannot take focus (focus stays in the box).
   */
  onLoadTypes?: () => void;
  /** Where the equipment-type catalogue stands; `ready` and `idle` show nothing. */
  typeStatus?: TitleBarSearchTypeStatus;
  /**
   * The key that focuses the box from anywhere outside a text field, drawn in it as a key cap
   * (`Search`'s own `shortcut`, `floor-plan-page-ds-inputs`). The Floor plan step passes `/`
   * (`floor-plan-page-stage`, R1); a host that omits it gets no key.
   */
  shortcut?: string;
  /** The box's width. Default `260px`; the Floor plan step passes the design's `328px` (R1). */
  width?: string;
  /**
   * The results list's width, right-aligned to the box. Default `360px`, wider than the box, for the
   * nav bar. A host inside a clipping panel passes `100%`, so the list stays inside the panel
   * (`onboarding-persistent-scene-floor-plan-glass`, R2.2).
   */
  listWidth?: string;
  /**
   * Whether a hit can be picked (`floor-plan-page-search`, R3.3). An unpickable hit is listed as an
   * unavailable option: arrow keys reach it, so its words are heard, but a click does nothing and
   * Enter announces `unpickableText` instead of picking. Default: every hit can be picked.
   */
  isPickable?: (hit: T) => boolean;
  /** What Enter on an unpickable hit announces. */
  unpickableText?: (hit: T) => string;
  /**
   * Told the query while the box has focus and holds one, and `null` once it is emptied, cleared,
   * left, or a pick closes the list — what the Floor plan lights on its plan (R4.1).
   */
  onActiveQueryChange?: (query: string | null) => void;
  /**
   * Enter with nothing highlighted and two or more hits (R5.1): the Floor plan frames every match.
   * Returns the sentence to announce. The list closes so it does not cover what was framed; focus,
   * the query and the host's light stay. Absent, that Enter does nothing, as before.
   */
  onSubmit?: (query: string) => string | undefined;
  /**
   * A line at the foot of the open list about a source the matches depend on — the Floor plan's
   * register while it loads or after it failed (R9). Its text is also added to the settled count.
   */
  sourceNote?: { busy?: boolean; tone?: 'error'; text: string };
  className?: string;
}

/** How long typing must settle before the match count is announced. */
const ANNOUNCE_SETTLE_MS = 300;

const TYPES_LOADING = 'Loading equipment types…';
const TYPES_FAILED =
  "Equipment types couldn't be loaded, so searching by type won't find anything.";
const TYPES_FAILED_SPOKEN = `${TYPES_FAILED} Leave the search box and come back to it to try again.`;
const TYPES_LOADED = 'Equipment types loaded.';

const countText = (n: number): string =>
  n === 0 ? 'No matches' : n === 1 ? '1 match' : `${n} matches`;

const Root = styled.div<{ $width: string; $listWidth: string }>`
  position: relative;
  width: ${p => p.$width};

  .plan-search-list {
    position: absolute;
    top: calc(100% + ${space[1]});
    right: 0;
    width: ${p => p.$listWidth};
    z-index: ${zIndex.dropdown};
  }
`;

export function TitleBarSearch<T>({
  label,
  placeholder,
  match,
  keyOf,
  option,
  emptyText,
  pickedText,
  onPick,
  onLoadTypes,
  typeStatus = 'idle',
  shortcut,
  width = '260px',
  listWidth = '360px',
  isPickable,
  unpickableText,
  onActiveQueryChange,
  onSubmit,
  sourceNote,
  className,
}: TitleBarSearchProps<T>) {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [highlightKey, setHighlightKey] = React.useState<string | null>(null);
  const [announcement, setAnnouncement] = React.useState('');
  const focusedOnce = React.useRef(false);
  const id = React.useId();
  const listId = `${id}-list`;
  const optionId = (i: number) => `${id}-opt-${i}`;

  const result = React.useMemo(() => match(query), [match, query]);
  const { hits, total } = result;
  const highlight = highlightKey == null ? -1 : hits.findIndex(h => keyOf(h) === highlightKey);
  const hasQuery = query.trim() !== '';
  const listOpen = open && hasQuery;
  const pickable = React.useCallback(
    (hit: T) => (isPickable ? isPickable(hit) : true),
    [isPickable],
  );

  /*
   * The query the page lights (R4.1): only while the box has focus and holds one, and not once a
   * pick has closed the list — the operator chose one entity and the selection shows it. Typing
   * again, or focusing the box again, lights the matches again.
   */
  const [picked, setPicked] = React.useState(false);
  const activeQuery = focused && hasQuery && !picked ? query.trim() : null;
  const onActiveQueryChangeRef = React.useRef(onActiveQueryChange);
  onActiveQueryChangeRef.current = onActiveQueryChange;
  React.useEffect(() => {
    onActiveQueryChangeRef.current?.(activeQuery);
  }, [activeQuery]);

  // The count, once typing settles — not per keystroke, which would talk over the typist. Only
  // while the list is open: a pick closes it, which cancels a pending count that would otherwise
  // overwrite "Selected …" (found by review).
  const noteText = sourceNote?.text;
  // The pending count, held so an action's announcement can cancel it: an explanation or a frame
  // said straight after typing must not be replaced by the count a moment later (review).
  const countTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => {
    if (!listOpen) return;
    countTimer.current = setTimeout(
      () => setAnnouncement(noteText ? `${countText(total)}. ${noteText}` : countText(total)),
      ANNOUNCE_SETTLE_MS,
    );
    return () => {
      if (countTimer.current) clearTimeout(countTimer.current);
      countTimer.current = null;
    };
  }, [listOpen, total, query, noteText]);

  /*
   * Keep the highlighted row in view: the panel scrolls (`MENU_MAX_HEIGHT`), and a highlight
   * below its fold leaves a sighted keyboard user unable to see what Enter will pick. The design
   * system's own lists do the same; `scrollIntoView` is feature-detected because jsdom has none.
   */
  React.useEffect(() => {
    if (!listOpen || highlight < 0) return;
    const el = document.getElementById(optionId(highlight));
    if (typeof el?.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- optionId is derived from a stable id
  }, [listOpen, highlight]);

  /*
   * The type load's outcome is said aloud too — on screen it is only a line at the foot of the list.
   * The region is emptied while a load runs, so a retry that fails AGAIN is spoken again rather than
   * swallowed as an unchanged string; and a load that succeeds after a failure says so.
   */
  const lastTypeStatus = React.useRef(typeStatus);
  const sawFailure = React.useRef(false);
  React.useEffect(() => {
    const previous = lastTypeStatus.current;
    lastTypeStatus.current = typeStatus;
    if (!focusedOnce.current || previous === typeStatus) return;
    if (typeStatus === 'loading') setAnnouncement('');
    else if (typeStatus === 'error') setAnnouncement(TYPES_FAILED_SPOKEN);
    else if (typeStatus === 'ready' && previous === 'loading' && sawFailure.current) {
      setAnnouncement(TYPES_LOADED);
    }
    if (typeStatus === 'error') sawFailure.current = true;
  }, [typeStatus]);

  /*
   * A second pick of the same hit is a real action (the Composite measures step jumps to the row
   * again), but the same sentence in an unchanged region is never spoken. Every other pick ends in a
   * no-break space, so the region's text always changes and each pick is announced
   * (`composites-page-search`, review).
   */
  const pickParity = React.useRef(false);
  const announce = (text: string) => {
    if (countTimer.current) clearTimeout(countTimer.current);
    countTimer.current = null;
    pickParity.current = !pickParity.current;
    setAnnouncement(text + (pickParity.current ? '' : '\u00a0'));
  };
  const pick = (hit: T) => {
    if (!pickable(hit)) {
      if (unpickableText) announce(unpickableText(hit));
      return;
    }
    onPick(hit);
    setOpen(false);
    setHighlightKey(null);
    setPicked(true);
    announce(pickedText(hit));
  };

  const move = (step: 1 | -1) => {
    if (hits.length === 0) return;
    setOpen(true);
    // The whole match set is back in front of the operator, so the host lights it again.
    setPicked(false);
    const next =
      highlight < 0
        ? step === 1
          ? 0
          : hits.length - 1
        : (highlight + step + hits.length) % hits.length;
    setHighlightKey(keyOf(hits[next]));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        move(-1);
        break;
      case 'Enter': {
        e.preventDefault();
        if (listOpen && highlight >= 0) {
          pick(hits[highlight]);
          break;
        }
        // Nothing highlighted: the only hit is picked, as before, when it can be; two or more hits
        // go to the page's submit — the Floor plan frames them (R5.1).
        if (hits.length === 1) {
          // `pick` explains an unpickable hit rather than staying silent (review).
          pick(hits[0]);
        } else if (onSubmit && hasQuery && total >= 2) {
          const said = onSubmit(query.trim());
          // The list closes so it does not cover what was just framed; focus, the query and the
          // page's light stay, and typing opens it again.
          setOpen(false);
          setHighlightKey(null);
          setPicked(false);
          if (said) announce(said);
        }
        break;
      }
      case 'Escape':
        // First Escape closes the list; `Search` clears only when nothing handled the key.
        if (listOpen) {
          e.preventDefault();
          setOpen(false);
          setHighlightKey(null);
        }
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const typesNote =
    typeStatus === 'loading' ? (
      <MenuState busy>{TYPES_LOADING}</MenuState>
    ) : typeStatus === 'error' ? (
      <MenuState
        tone="error"
        action={
          onLoadTypes ? (
            <Button variant="secondary" size="inline" onClick={onLoadTypes}>
              Retry
            </Button>
          ) : undefined
        }
      >
        {TYPES_FAILED}
      </MenuState>
    ) : null;

  return (
    <Root
      className={className ? `plan-search ${className}` : 'plan-search'}
      $width={width}
      $listWidth={listWidth}
    >
      <Search
        size="sm"
        shortcut={shortcut}
        aria-label={label}
        placeholder={placeholder}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={listOpen}
        aria-controls={listId}
        aria-activedescendant={listOpen && highlight >= 0 ? optionId(highlight) : undefined}
        autoComplete="off"
        spellCheck={false}
        value={query}
        onChange={v => {
          setQuery(v);
          setOpen(true);
          setHighlightKey(null);
          setPicked(false);
        }}
        onClear={() => {
          setOpen(false);
          setHighlightKey(null);
          setAnnouncement('');
        }}
        onKeyDown={onKeyDown}
        onFocus={() => {
          setFocused(true);
          setPicked(false);
          if (!focusedOnce.current || typeStatus === 'error') {
            focusedOnce.current = true;
            onLoadTypes?.();
          }
          if (hasQuery) setOpen(true);
        }}
        onBlur={() => {
          setOpen(false);
          setFocused(false);
        }}
      />
      {listOpen && (
        <Menu
          id={listId}
          role="listbox"
          aria-label="Matches"
          className="plan-search-list"
          // A press on the panel (a row, the Retry) must not blur the box before its click lands.
          onMouseDown={e => e.preventDefault()}
        >
          {hits.length === 0 ? (
            <MenuState>{emptyText(query.trim())}</MenuState>
          ) : (
            hits.map((hit, i) => {
              const o = option(hit);
              const canPick = pickable(hit);
              return (
                <MenuItem
                  key={keyOf(hit)}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === highlight}
                  aria-label={o.name}
                  highlighted={i === highlight}
                  // Unavailable to assistive tech, but in normal ink: these rows exist to be
                  // read — the answer to "where is asset X" — and frame 08 draws them so. The
                  // menu's dimmed `disabled` look is for options that are off (review).
                  aria-disabled={canPick ? undefined : true}
                  icon={<Icon name={o.icon} variant={o.iconVariant} size="sm" />}
                  meta={o.meta}
                  trailing={
                    o.trailing && o.trailingTone ? (
                      <MenuTrailingLabel tone={o.trailingTone}>{o.trailing}</MenuTrailingLabel>
                    ) : (
                      o.trailing
                    )
                  }
                  onMouseEnter={() => setHighlightKey(keyOf(hit))}
                  onClick={canPick ? () => pick(hit) : undefined}
                >
                  {o.label}
                </MenuItem>
              );
            })
          )}
          {total > hits.length && (
            <MenuCap>{`Showing ${hits.length} of ${total}. Keep typing to narrow it.`}</MenuCap>
          )}
          {sourceNote && (
            <MenuState busy={sourceNote.busy} tone={sourceNote.tone}>
              {sourceNote.text}
            </MenuState>
          )}
          {typesNote}
        </Menu>
      )}
      <div role="status" aria-live="polite" style={visuallyHidden}>
        {announcement}
      </div>
    </Root>
  );
}
